// Constants and Utils
import { calculatePercentage } from "../../../common-utils/number-utils/NumberUtils";
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";
import {
  getIsSubscriptionActive,
  maskCharacter,
} from "../../../common-utils/subscription-utils/SubscriptionUtil";
import { ProgressBar } from "../../../components/progress-bar/ProgressBar";
import { UrlKeys } from "../../../constants/GeneralConstants";
import {
  getFrequencyValue,
  getModifiedPopulation,
  // getReachPercentage,
  getReachValueAsString,
} from "../../../utils/ReachAndFrequencyUtils";
import { useUrlKeyCheck } from "../../utils/hooks/HooksUtil";

// Components
import Rating from "./Rating";

function getFreqReachValue(isTgSelected, freqReachValue) {
  // if no tg selected then show NA
  if (!isTgSelected) {
    return "NA";
  }

  return formatText(toLocaleString(freqReachValue));
}

function FrequencySection({ reachByFreq, label, isTgSelected = true }) {
  const hasImpressions = reachByFreq && Object.keys(reachByFreq).length > 0;

  const {
    frequencyReach1,
    frequencyReach2,
    frequencyReach3,
    frequencyReach4,
    frequencyReach5,
  } = hasImpressions ? reachByFreq : {};

  return (
    <div className="col-2">
      <p>{label}</p>
      {!hasImpressions && <p>--</p>}
      {hasImpressions && (
        <>
          <h5> F 1+ : {getFreqReachValue(isTgSelected, frequencyReach1)}</h5>
          <h5> F 2+ : {getFreqReachValue(isTgSelected, frequencyReach2)}</h5>
          <h5> F 3+ : {getFreqReachValue(isTgSelected, frequencyReach3)}</h5>
          <h5> F 4+ : {getFreqReachValue(isTgSelected, frequencyReach4)}</h5>
          <h5> F 5+ : {getFreqReachValue(isTgSelected, frequencyReach5)}</h5>
        </>
      )}
    </div>
  );
}

// tg impressions string
export function getTgImpressionsString(targetGroupId, isSubscribed, tgOts) {
  // when no tg selected
  if (!targetGroupId) {
    return "NA";
  }

  // when not subscribed
  if (!isSubscribed) {
    return maskCharacter(tgOts);
  }

  // else
  return formatText(toLocaleString(tgOts));
}

// generic impressions string
export function getImpressionsString(isSubscribed, ots) {
  // when not subscribed
  if (!isSubscribed) {
    return maskCharacter(ots);
  }

  // else
  return formatText(toLocaleString(ots));
}

export function MonthlyImpressions({
  impressions,
  duration,
  targetGroupId,
  isCustomTgSelected = false,
}) {
  // Checking Org is Subscribed or Not
  const isSubscribed = getIsSubscriptionActive();
  const { ots, tgOts } = impressions;
  const tgOtsPercentage = calculatePercentage(tgOts, ots);

  // final tg select check
  const isAnyTgSelected = targetGroupId || isCustomTgSelected;

  const finalImpStr = getImpressionsString(isSubscribed, ots);
  const finalTgImpStr = getTgImpressionsString(
    isAnyTgSelected,
    isSubscribed,
    tgOts
  );

  return (
    <div className="col-3">
      <p>Impressions ({duration} Days)</p>
      <div className="d-flex align-items-center mb-2 fs-6">
        <div className="col-4 pl-0">
          <span className="d-inline-block border border-dark square bg-light-green mr-2"></span>
          TG
        </div>
        <span className="col-4">{finalTgImpStr}</span>
      </div>
      <div className="d-flex align-items-center mb-2 fs-6">
        <div className="col-4 pl-0">
          <span className="d-inline-block border border-dark square bg-light-blue mr-2"></span>
          Total
        </div>
        <span className="col-8">{finalImpStr}</span>
      </div>

      {/* bars */}
      {parseInt(ots) > 0 && (
        <>
          <ProgressBar
            className="bg-light-green"
            percentage={tgOtsPercentage}
          />
          <ProgressBar className="bg-light-blue" percentage={100} />
        </>
      )}
    </div>
  );
}

export function MediaReach({
  reachFrequency = {},
  //  cityPopulation,
  targetGroupId,
  isCustomTgSelected = false,
}) {
  // original reach, tgReach
  const { reach, tgReach } = reachFrequency || {};
  const tgReachPercentWithBaseReach = calculatePercentage(
    Number(tgReach),
    Number(reach)
  );

  // as string
  const { reach: reachString, tgReach: tgReachString } =
    getReachValueAsString(reachFrequency);

  // final tg select check
  const isAnyTgSelected = targetGroupId || isCustomTgSelected;

  // final tg reach string
  const finalTgReachString = isAnyTgSelected ? formatText(tgReachString) : "NA";

  return (
    <div className="col-3">
      <p>Reach</p>
      <div className="d-flex align-items-center mb-2 fs-6">
        <div className="col-4 pl-0">
          <span className="d-inline-block border border-dark mr-2 square bg-light-pink"></span>
          TG
        </div>
        <span className="col-4">{finalTgReachString}</span>
      </div>
      <div className="d-flex align-items-center mb-2 fs-6">
        <div className="col-4 pl-0">
          <span className="d-inline-block border border-dark mr-2 square bg-light-yellow"></span>
          Total
        </div>
        <span className="col-4">{formatText(reachString)}</span>
      </div>

      {/* Bars */}
      {parseInt(reach) > 0 && (
        <>
          <ProgressBar
            className="bg-light-pink"
            percentage={tgReachPercentWithBaseReach}
          />
          <ProgressBar className="bg-light-yellow" percentage={100} />
        </>
      )}
    </div>
  );
}

export function CityPopulation({ cityPopulation }) {
  const population = getModifiedPopulation(cityPopulation);
  return (
    <div className="col-2">
      <p>
        <span className="d-block">{"City Population"}</span>
      </p>
      <h5>{population}</h5>
    </div>
  );
}

export function MediaFrequency({ reachFrequency = {} }) {
  // Checking if it is from Site-analyser tool
  const isFromSiteAnalyserTool = useUrlKeyCheck(UrlKeys.siteAnalyserTool);

  // for site-analyser-tool
  if (isFromSiteAnalyserTool) {
    const unCumulateReachByFreq = reachFrequency?.unCumulateReachByFreq || {};
    return (
      <FrequencySection
        label="Frequency - Reach"
        reachByFreq={{
          frequencyReach1: unCumulateReachByFreq["1"],
          frequencyReach2: unCumulateReachByFreq["2"],
          frequencyReach3: unCumulateReachByFreq["3"],
          frequencyReach4: unCumulateReachByFreq["4"],
          frequencyReach5: unCumulateReachByFreq["5"],
        }}
      />
    );
  }

  // other than tool
  const {
    frequencyReach1,
    frequencyReach2,
    frequencyReach3,
    frequencyReach4,
    frequencyReach5,
  } = reachFrequency || {};

  return (
    <FrequencySection
      label="Frequency - Reach"
      reachByFreq={{
        frequencyReach1,
        frequencyReach2,
        frequencyReach3,
        frequencyReach4,
        frequencyReach5,
      }}
    />
  );
}

export function MediaTgFrequency({
  reachFrequency = {},
  targetGroupId,
  isCustomTgSelected = false,
}) {
  // Checking if it is from Site-analyser tool
  const isFromSiteAnalyserTool = useUrlKeyCheck(UrlKeys.siteAnalyserTool);

  // final tg select check
  const isAnyTgSelected = targetGroupId || isCustomTgSelected;

  // for site-analyser-tool
  if (isFromSiteAnalyserTool) {
    const unCumulateTgReachByFreq =
      reachFrequency?.unCumulateTgReachByFreq || {};
    return (
      <FrequencySection
        label="Frequency - TG Reach"
        reachByFreq={{
          frequencyReach1: unCumulateTgReachByFreq["1"],
          frequencyReach2: unCumulateTgReachByFreq["2"],
          frequencyReach3: unCumulateTgReachByFreq["3"],
          frequencyReach4: unCumulateTgReachByFreq["4"],
          frequencyReach5: unCumulateTgReachByFreq["5"],
        }}
        isTgSelected={!!isAnyTgSelected}
      />
    );
  }

  // other than tool
  const {
    frequencyTgReach1,
    frequencyTgReach2,
    frequencyTgReach3,
    frequencyTgReach4,
    frequencyTgReach5,
  } = reachFrequency || {};

  return (
    <FrequencySection
      label="Frequency - TG Reach"
      reachByFreq={{
        frequencyReach1: frequencyTgReach1,
        frequencyReach2: frequencyTgReach2,
        frequencyReach3: frequencyTgReach3,
        frequencyReach4: frequencyTgReach4,
        frequencyReach5: frequencyTgReach5,
      }}
      isTgSelected={!!isAnyTgSelected}
    />
  );
}

export function OtherDetails({ ltsRating, dwellTimeSummary, reachFrequency }) {
  const { averageTime } = dwellTimeSummary ?? {};
  const frequencyValue = getFrequencyValue(reachFrequency);

  return (
    <div className="col-3 mt-3">
      <p>Other Details</p>
      <h5> LTS rating : {formatText(ltsRating)}</h5>
      <h5>
        Avg. Dwell Time :{" "}
        {averageTime ? Math.floor(averageTime) : formatText(averageTime)} sec
      </h5>
      <h5>Frequency : {frequencyValue} </h5>
    </div>
  );
}

// ArAC ==> Ar(AspectRation), A(Area), C(Clutter)
export function ArAC_ratings({ ltsSummary }) {
  const {
    areaIndex,
    clutterIndex,
    aspectRatioIndex,
    elevationIndex,
    tiltIndex,
  } = ltsSummary || {};

  const ltsInfoConstants = [
    {
      label: "Aspect Ratio",
      value: aspectRatioIndex,
    },
    {
      label: "Area",
      value: areaIndex,
    },
    {
      label: "Clutter",
      value: clutterIndex,
    },
    {
      label: "Elevation",
      value: elevationIndex,
    },
    {
      label: "Tilt",
      value: tiltIndex,
    },
  ];

  return (
    <div className="col-4 mt-3">
      {ltsInfoConstants.map((eachInfo) => (
        <div key={eachInfo.label} className="mb-2">
          <Rating displayName={eachInfo.label} value={eachInfo.value} />
        </div>
      ))}
    </div>
  );
}
